import { Edit3 } from 'lucide-react';
import React, { useState } from 'react';
import { Card as BootstrapCard, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { CardAction } from '../../service/api/types';
import { CardActionsEditor } from '../CardActionsEditor';

interface InlineCardEditorProps {
  label: string;
  actions: CardAction[];
  onChange: (actions: CardAction[]) => void;
  required?: boolean;
}

export const InlineCardEditor: React.FC<InlineCardEditorProps> = ({
  label,
  actions,
  onChange,
  required = false,
}) => {
  const { t } = useTranslation();
  const [showEditor, setShowEditor] = useState(false);

  const getActionsSummary = () => {
    if (actions.length === 0) {
      return required
        ? t('cards.actions.noActionsDefinedRequired')
        : t('cards.actions.noActionsDefined');
    }

    return actions
      .map(action => {
        if (action.type === 'sticker') {
          return action.amount > 0
            ? t('cards.actions.giveStickers', {
                count: Math.abs(action.amount),
              })
            : t('cards.actions.takeStickers', {
                count: Math.abs(action.amount),
              });
        } else if (action.type === 'item') {
          const itemName =
            typeof action.item === 'string'
              ? t('common.item')
              : action.item?.title || t('common.item');
          return t('cards.actions.giveItems', {
            count: action.amount,
            name: itemName,
          });
        } else {
          // effect action
          const effectLabel =
            action.effectType === 'set_price'
              ? t('effects.setPrice')
              : action.effectType === 'adjust_all_prices_percent'
                ? t('effects.adjustAllPrices')
                : t('effects.adjustItemPrice');
          return `⚡ ${effectLabel} (${action.value > 0 ? '+' : ''}${action.value}${action.effectType !== 'set_price' ? '%' : ''})`;
        }
      })
      .join(', ');
  };

  return (
    <>
      <BootstrapCard className="mb-3">
        <BootstrapCard.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>{label}</strong>
              <div
                className={`small ${actions.length === 0 && required ? 'text-danger' : 'text-muted'}`}
              >
                {getActionsSummary()}
              </div>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowEditor(true)}
            >
              <Edit3 size={16} className="me-1" />
              {t('common.edit')}
            </Button>
          </div>
        </BootstrapCard.Body>
      </BootstrapCard>

      <CardActionsEditor
        show={showEditor}
        onHide={() => setShowEditor(false)}
        onSave={onChange}
        initialActions={actions}
        title={`${t('common.edit')} ${label}`}
      />
    </>
  );
};
